import {
  Box,
  Button,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  type RestoreAccount,
  type SourceAccount,
  CloudAccountType,
  type Account,
} from '@repo/api-gw-sdk';
import { useState } from 'react';

import { useDAL } from '@/contexts/useDAL';
import {
  getAccountType,
  isRestoreAccount,
  isSourceAccount,
} from '@/utils/accounts';
import {
  getAccountInstalledVersion,
  getAccountLatestVersion,
} from '@/utils/roles';

import { TerraformBashToggle } from './terraformBashToggle';
import { useSyncAccountPermissions } from './useSyncAccountPermissions';

import { Loader } from '../layout/loading';
import { PanelWrapper } from '../panels/panelWrapper';
import { CopyableCode } from '../shared/copyableCode';

const BLOB_BASE = 'https://eon.blob.core.windows.net/public/onboarding/latest';

function getModuleName(accountType: CloudAccountType) {
  return accountType === CloudAccountType.Source ? 'source' : 'restore';
}

export const UpdateAccountAzurePanel = ({
  account,
  onUpdated,
  onClose,
}: {
  account: Account | SourceAccount | RestoreAccount;
  onUpdated: () => void;
  onClose: () => void;
}) => {
  const [deploymentMethod, setDeploymentMethod] = useState<
    'terraform' | 'bash'
  >('terraform');
  const [isValidationInProgress, setValidationInProgress] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);

  const dal = useDAL();
  const accountType = getAccountType(account);
  const tenantId = isSourceAccount(account)
    ? account.sourceAccountAttributes.azure?.tenantId
    : isRestoreAccount(account)
      ? account.restoreAccountAttributes.azure?.tenantId
      : account.tenantId;
  const accountTypeKey =
    accountType === CloudAccountType.Source ? 'Source' : 'Restore';
  const installedVersion = getAccountInstalledVersion(account);
  const latestVersion = getAccountLatestVersion(account);
  const { data: backupApps } = dal.cloudAccounts.azureApps.useApps(
    accountTypeKey,
    tenantId
  );

  const { syncAccountPermissions } = useSyncAccountPermissions(
    () => {
      setIsUpdated(true);
      setTimeout(onUpdated, 1000);
    },
    setValidationInProgress,
    setHasError,
    account,
    true
  );

  const moduleName = getModuleName(accountType);
  const subscriptionId = account.providerAccountId;
  const managementAppId = backupApps?.managementAppId ?? '';
  const backupAppId = backupApps?.backupAppId ?? '';
  const eonInternalRgName = isSourceAccount(account)
    ? account.sourceAccountAttributes?.azure?.eonInternalResourceGroupName
    : isRestoreAccount(account)
      ? account.restoreAccountAttributes?.azure?.eonInternalResourceGroupName
      : undefined;

  const eonInternalRgBashArg = eonInternalRgName
    ? ` \\
  --eon-internal-resource-group "${eonInternalRgName}"`
    : '';

  const bashCode = `\
# Log in to the tenant
az login --tenant "${tenantId}"

# Set the subscription
az account set --subscription "${subscriptionId}"

# Get access tokens from Azure
ARM_TOKEN=$(az account get-access-token --query accessToken -o tsv)
GRAPH_TOKEN=$(az account get-access-token --resource https://graph.microsoft.com/ --query accessToken -o tsv)

# Download and run the ${moduleName} script
curl -fsSL ${BLOB_BASE}/${moduleName}/bash/${moduleName}.sh -o ${moduleName}.sh && chmod +x ${moduleName}.sh && \\
AZURE_ACCESS_TOKEN="$ARM_TOKEN" \\
AZURE_GRAPH_TOKEN="$GRAPH_TOKEN" \\
./${moduleName}.sh \\
  --subscription-id "${subscriptionId}" \\
  --management-app-id "${managementAppId}" \\
  --backup-app-id "${backupAppId}"${eonInternalRgBashArg}`;

  const eonInternalRgTfLine = eonInternalRgName
    ? `\n  eon_internal_resource_group_name = "${eonInternalRgName}"`
    : '';

  const terraformCode = `\
module "azure_${moduleName}" {
  source = "${BLOB_BASE}/${moduleName}/terraform/${moduleName}.zip"
  subscription_id        = "${subscriptionId}"
  management_app_id      = "${managementAppId}"
  backup_${moduleName === 'restore' ? 'restore_' : ''}app_id${moduleName === 'restore' ? '   ' : '          '} = "${backupAppId}"${eonInternalRgTfLine}
}

output "${moduleName}_onboarding" {
  value = module.azure_${moduleName}
}`;

  const code = deploymentMethod === 'terraform' ? terraformCode : bashCode;

  return (
    <PanelWrapper rounded header={{ title: 'Update permissions', onClose }}>
      <CardContent>
        <Stack direction='column' gap='16px'>
          <Box>
            <b>Account</b>: {subscriptionId}
          </Box>
          <Box>
            <b>Currently installed</b>: {installedVersion ?? 'unknown'}
            <br />
            <b>Latest version</b>: {latestVersion}
          </Box>
          <Divider />
          <TerraformBashToggle
            text='Re-run the onboarding script to update permissions to the latest version.'
            connectMethod={deploymentMethod}
            setConnectMethod={setDeploymentMethod}
          />
          <CopyableCode code={code} codeClassName='whitespace-pre-wrap' />
          <Divider />
          <Typography variant='body2'>
            After running the script, click <b>I&apos;m Done</b> so Eon can
            confirm the new permissions.
          </Typography>

          {!hasError && !isValidationInProgress && !isUpdated && (
            <Button
              variant='contained'
              data-testid='azure-update-done'
              onClick={() => void syncAccountPermissions()}
            >
              I&apos;m Done
            </Button>
          )}
          {isValidationInProgress && (
            <Box className='flex items-center gap-[8px]'>
              <Loader variant='inline' />
              <Typography variant='body2'>Verifying permissions...</Typography>
            </Box>
          )}
          {isUpdated && !isValidationInProgress && (
            <Box
              className='flex items-center gap-[4px] h-[48px]'
              sx={{ color: 'var(--mui-palette-success-main)' }}
            >
              <i className='material-symbols-check w-[20px] h-[20px]' /> Account
              updated
            </Box>
          )}
          {hasError && !isValidationInProgress && (
            <Box
              className='flex items-center gap-[4px] h-[48px]'
              sx={{ color: 'var(--mui-palette-error-main)' }}
            >
              <i className='material-symbols-close-rounded w-[20px] h-[20px]' />{' '}
              Something went wrong
              <Divider
                orientation='vertical'
                flexItem
                className='mx-[6px] my-auto h-[20px]'
              />
              <Button
                variant='contained'
                onClick={() => {
                  setHasError(false);
                  void syncAccountPermissions();
                }}
              >
                Try Again
              </Button>
            </Box>
          )}
        </Stack>
      </CardContent>
    </PanelWrapper>
  );
};
